import { useEffect, useState } from "react";
import { Check, FileText, Plus, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL, QUOTE_STATUS, RFQ_STATUS } from "../lib/api";
import { fmtQty4 } from "../lib/format";
import QualityBadge from "../components/QualityBadge";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import ConfirmDialog from "../components/ConfirmDialog";

export default function Rfqs() {
    const { user } = useAuth();
    const [rfqs, setRfqs] = useState(null);
    const [detail, setDetail] = useState(null);
    const [quoteDlg, setQuoteDlg] = useState(null);
    const [prices, setPrices] = useState({});
    const [delDays, setDelDays] = useState({});
    const [quoteNote, setQuoteNote] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [newItems, setNewItems] = useState([{ product_id: "", qty: 10 }]);
    const [newNote, setNewNote] = useState("");
    const [products, setProducts] = useState([]);
    const [confirmAccept, setConfirmAccept] = useState(null);
    const isSupplier = user?.role === "supplier";

    const load = () => api.get("/rfqs").then((r) => setRfqs(r.data));
    useEffect(() => { load().catch(() => {}); api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

    const openDetail = async (rfq) => {
        const r = await api.get(`/rfqs/${rfq.rfq_id}`);
        setDetail(r.data);
    };

    const submitQuote = async () => {
        try {
            const items = quoteDlg.items.map((it) => ({ product_id: it.product_id, unit_price: Number(prices[it.product_id]), delivery_days: Number(delDays[it.product_id]) || undefined }));
            if (items.some((i) => !i.unit_price || i.unit_price <= 0)) return toast.error("Tüm kalemler için geçerli fiyat girin");
            await api.post(`/rfqs/${quoteDlg.rfq_id}/quotes`, { items, note: quoteNote, valid_days: 7 });
            toast.success("Teklifiniz gönderildi");
            setQuoteDlg(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Teklif gönderilemedi");
        }
    };

    const acceptQuote = async (quoteId) => {
        try {
            const r = await api.post(`/quotes/${quoteId}/accept`);
            toast.success(`Teklif kabul edildi — Sipariş: ${r.data.order.order_no}`);
            setDetail(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        }
    };

    const rejectQuote = async (quoteId) => {
        await api.post(`/quotes/${quoteId}/reject`);
        toast.success("Teklif reddedildi");
        openDetail(detail);
    };

    const createRfq = async () => {
        try {
            const items = newItems.filter((i) => i.product_id && i.qty > 0);
            if (!items.length) return toast.error("En az bir ürün seçin");
            await api.post("/rfqs", { items, note: newNote, validity_days: 7 });
            toast.success("Teklif talebi oluşturuldu");
            setCreateOpen(false);
            setNewItems([{ product_id: "", qty: 10 }]);
            setNewNote("");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Oluşturulamadı");
        }
    };

    return (
        <div className="space-y-6" data-testid="rfqs-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">{isSupplier ? "Gelen Teklif Talepleri" : "Teklif Taleplerim (RFQ)"}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {isSupplier ? "Açık taleplere fiyat verin, kazanırsanız sipariş otomatik oluşur." : "Talep oluşturun, gelen teklifleri karşılaştırıp kabul edin."}
                    </p>
                </div>
                {!isSupplier && (
                    <Button onClick={() => setCreateOpen(true)} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="rfq-create-button">
                        <Plus className="mr-2 h-4 w-4" /> Yeni Talep
                    </Button>
                )}
            </div>

            {!rfqs ? (
                <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="space-y-3" data-testid="rfq-list">
                    {rfqs.map((r) => (
                        <div key={r.rfq_id} className="flex flex-wrap items-center gap-4 rounded-2xl border bg-card p-5 shadow-sm" data-testid={`rfq-card-${r.rfq_id}`}>
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted"><FileText className="h-5 w-5 text-terra-600" /></div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="font-display font-bold">{r.rfq_no}</p>
                                    <Badge variant="outline" className={`text-[10px] ${RFQ_STATUS[r.status]?.cls}`} data-testid={`rfq-status-${r.rfq_id}`}>{RFQ_STATUS[r.status]?.label}</Badge>
                                </div>
                                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                    {r.buyer_company_name} • {r.items.length} kalem • {fmtDate(r.created_at)} • {r.quote_count} teklif
                                </p>
                                <p className="mt-1 truncate text-xs text-muted-foreground">{r.items.map((i) => `${i.name}${i.quality ? ` [${i.quality}]` : ""} (${fmtQty4(i.qty, i.unit)} ${i.unit})`).join(", ")}</p>
                            </div>
                            <div className="flex gap-2">
                                {isSupplier && ["open", "quoted"].includes(r.status) && !r.my_quote && (
                                    <Button size="sm" className="bg-terra-600 hover:bg-terra-700 text-white" onClick={() => { setQuoteDlg(r); setPrices({}); setQuoteNote(""); }} data-testid={`rfq-quote-button-${r.rfq_id}`}>
                                        <Send className="mr-1.5 h-3.5 w-3.5" /> Teklif Ver
                                    </Button>
                                )}
                                {isSupplier && r.my_quote && <Badge variant="outline" className={`${QUOTE_STATUS[r.my_quote.status]?.cls}`}>Teklifim: {fmtTL(r.my_quote.total)} — {QUOTE_STATUS[r.my_quote.status]?.label}</Badge>}
                                <Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`rfq-detail-${r.rfq_id}`}>Detay</Button>
                            </div>
                        </div>
                    ))}
                    {!rfqs.length && <p className="rounded-2xl border border-dashed py-16 text-center text-sm text-muted-foreground" data-testid="rfq-empty">Teklif talebi yok.</p>}
                </div>
            )}

            <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
                <DialogContent className="max-w-3xl" data-testid="rfq-detail-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display">{detail.rfq_no} — Teklif Karşılaştırma</DialogTitle>
                            </DialogHeader>
                            <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
                                {detail.items.map((i) => `${i.name}${i.quality ? ` [${i.quality}]` : ""}: ${fmtQty4(i.qty, i.unit)} ${i.unit}`).join(" • ")}
                                {detail.note && <p className="mt-1">Not: {detail.note}</p>}
                            </div>
                            {detail.quotes.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Henüz teklif gelmedi.</p>}
                            <div className="space-y-3">
                                {detail.quotes.map((q, i) => (
                                    <div key={q.quote_id} className={`rounded-xl border p-4 ${i === 0 && q.status === "pending" ? "border-emerald-300 bg-emerald-50/50" : ""}`} data-testid={`quote-row-${q.quote_id}`}>
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div>
                                                <p className="font-semibold">{q.supplier_name} <span className="text-xs font-normal text-muted-foreground">({q.supplier_city}) • Güven {q.trust_score}</span></p>
                                                <p className="text-xs text-muted-foreground">{q.items.map((it) => {
                                                    const prod = detail.items.find((d) => d.product_id === it.product_id);
                                                    return `${prod?.name?.split("(")[0]}: ${fmtTL(it.unit_price)}${it.delivery_days ? ` — ${it.delivery_days} gün teslim` : ""}`;
                                                }).join(" • ")}</p>
                                                {q.note && <p className="mt-1 text-xs italic text-muted-foreground">"{q.note}"</p>}
                                            </div>
                                            <div className="text-right">
                                                <p className="font-display text-xl font-extrabold text-terra-600">{fmtTL(q.total)}</p>
                                                <Badge variant="outline" className={`text-[10px] ${QUOTE_STATUS[q.status]?.cls}`}>{QUOTE_STATUS[q.status]?.label}</Badge>
                                            </div>
                                        </div>
                                        {!isSupplier && q.status === "pending" && detail.status !== "accepted" && (
                                            <div className="mt-3 flex justify-end gap-2 border-t pt-3">
                                                <Button size="sm" variant="outline" onClick={() => rejectQuote(q.quote_id)} data-testid={`quote-reject-${q.quote_id}`}><X className="mr-1 h-3.5 w-3.5" /> Reddet</Button>
                                                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setConfirmAccept(q)} data-testid={`quote-accept-${q.quote_id}`}><Check className="mr-1 h-3.5 w-3.5" /> Kabul Et & Sipariş Oluştur</Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={!!quoteDlg} onOpenChange={() => setQuoteDlg(null)}>
                <DialogContent data-testid="quote-dialog">
                    {quoteDlg && (
                        <>
                            <DialogHeader><DialogTitle className="font-display">Teklif Ver — {quoteDlg.rfq_no}</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                {quoteDlg.items.map((it) => (
                                    <div key={it.product_id} className="flex items-center justify-between gap-3">
                                        <div className="text-sm"><p className="font-semibold">{it.name} <QualityBadge quality={it.quality} tid={it.product_id} /></p><p className="text-xs text-muted-foreground">Miktar: {fmtQty4(it.qty, it.unit)} {it.unit}</p></div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-xs text-muted-foreground">₺</span>
                                            <Input type="number" className="h-9 w-28" placeholder="birim fiyat" value={prices[it.product_id] ?? ""} onChange={(e) => setPrices({ ...prices, [it.product_id]: e.target.value })} data-testid={`quote-price-${it.product_id}`} />
                                            <span className="text-xs text-muted-foreground">/ {it.unit}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Input type="number" min={1} className="h-9 w-20" placeholder="gün" value={delDays[it.product_id] ?? ""} onChange={(e) => setDelDays({ ...delDays, [it.product_id]: e.target.value })} data-testid={`quote-days-${it.product_id}`} />
                                            <span className="text-xs text-muted-foreground">günde teslim</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Textarea value={quoteNote} onChange={(e) => setQuoteNote(e.target.value)} placeholder="Teklif notu (teslim süresi, navlun vb.)" data-testid="quote-note" />
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setQuoteDlg(null)}>Vazgeç</Button>
                                <Button onClick={submitQuote} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="quote-submit">Teklifi Gönder</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!confirmAccept}
                onClose={() => setConfirmAccept(null)}
                onConfirm={() => { const q = confirmAccept; setConfirmAccept(null); acceptQuote(q.quote_id); }}
                title="Teklif kabul ediliyor"
                description={confirmAccept ? `${confirmAccept.supplier_name} tedarikçisinin ${fmtTL(confirmAccept.total)} tutarındaki teklifini kabul etmek üzeresiniz. Sipariş otomatik oluşturulacak ve stok ayrılacak. Onaylıyor musunuz?` : ""}
                confirmText="Evet, Kabul Et"
                testId="quote-accept-confirm"
            />

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent data-testid="rfq-create-dialog">
                    <DialogHeader><DialogTitle className="font-display">Yeni Teklif Talebi</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        {newItems.map((row, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <select
                                    className="h-10 flex-1 rounded-md border bg-background px-3 text-sm"
                                    value={row.product_id}
                                    onChange={(e) => setNewItems(newItems.map((r, i) => (i === idx ? { ...r, product_id: e.target.value } : r)))}
                                    data-testid={`rfq-new-product-${idx}`}
                                >
                                    <option value="">Ürün seçin...</option>
                                    {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} ({p.unit})</option>)}
                                </select>
                                <Input type="number" className="w-24" value={row.qty} min={1} onChange={(e) => setNewItems(newItems.map((r, i) => (i === idx ? { ...r, qty: Number(e.target.value) } : r)))} data-testid={`rfq-new-qty-${idx}`} />
                                {newItems.length > 1 && (
                                    <Button variant="ghost" size="icon" onClick={() => setNewItems(newItems.filter((_, i) => i !== idx))}><X className="h-4 w-4" /></Button>
                                )}
                            </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => setNewItems([...newItems, { product_id: "", qty: 10 }])} data-testid="rfq-add-row"><Plus className="mr-1 h-3.5 w-3.5" /> Kalem Ekle</Button>
                        <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Tedarikçilere not..." data-testid="rfq-new-note" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>Vazgeç</Button>
                        <Button onClick={createRfq} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="rfq-create-submit">Talebi Gönder</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
